import {
  Box,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  Text,
  Tooltip,
  useCheckbox,
  useCheckboxGroup,
  UseCheckboxProps,
  VStack,
} from "@chakra-ui/react";
import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxCheckedIcon } from "../icons/CheckboxCheckedIcon";
import { CheckboxUncheckedIcon } from "../icons/CheckboxUncheckedIcon";
import { LockIcon } from "../icons/LockIcon";
import { SearchIcon } from "../icons/SearchIcon";
import { ProtectedImage } from "../ProtectedImage";
import { List, ListItem } from "../../api/lists";
import { ENTITY_TYPES } from "../../screens/root/constants";

interface SearchableMultiSelectProps {
  lists: List[];
  /**
   * Lists the entity should be a member of AFTER Apply is clicked.
   * Initial value is the current membership; toggling a row's checkbox
   * adds or removes the list's id from this set. The parent batches the
   * diff against the baseline on Apply.
   */
  selectedListIds: number[];
  onSelectionChange: (listIds: number[]) => void;
  listType: "comics" | "releases";
  entityId: number;
}

// Single-checkbox model. Every list renders as one row: the checkbox is
// initially checked when the entity is already a member, unchecked
// otherwise. Toggling adjusts the parent's `selectedListIds` set, which it
// diffs against baseline membership on Apply to produce the add+remove
// mutations. Replaces the prior split UI where already-in lists had an
// explicit X-to-remove button and to-add lists had a separate checkbox —
// two interaction models for adjacent rows was confusing and lacked
// confirm/undo on the destructive direction.
export function SearchableMultiSelect({
  lists,
  selectedListIds,
  onSelectionChange,
  listType,
  entityId,
}: SearchableMultiSelectProps) {
  const [t] = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");

  // Membership check — does the list already contain this entity? Used
  // for the "In list" badge so the user can distinguish a list they're
  // about to add to from one they're about to keep / remove from. Reads
  // list.list_items directly because the per-user list endpoints feeding
  // this surface ship full items (see backend handle_get_comics_lists).
  const isEntityInList = useCallback(
    (list: List): boolean => {
      if (!list.list_items || !Array.isArray(list.list_items)) {
        return false;
      }
      const expectedEntityTypeId =
        listType === "comics" ? ENTITY_TYPES.COMICS : ENTITY_TYPES.RELEASES;
      return list.list_items.some(
        (item) =>
          item.context_entity_id === entityId &&
          item.context_entity_type_id === expectedEntityTypeId,
      );
    },
    [entityId, listType],
  );

  const filteredLists = useMemo(() => {
    if (!lists || !Array.isArray(lists)) return [];
    if (!searchTerm) return lists;
    const needle = searchTerm.toLowerCase();
    return lists.filter((list) =>
      list.display_name.toLowerCase().includes(needle),
    );
  }, [lists, searchTerm]);

  const selectedSet = useMemo(
    () => new Set(selectedListIds),
    [selectedListIds],
  );

  const { getCheckboxProps } = useCheckboxGroup({
    value: selectedListIds.map((id) => id.toString()),
    onChange: (values: string[]) => {
      onSelectionChange(values.map((id) => parseInt(id, 10)));
    },
  });

  if (lists.length === 0) {
    // Empty-state copy — distinct from "no results" so the user knows
    // they haven't created any lists yet, not that their query missed.
    return (
      <VStack align="stretch" px={3} py={4}>
        <Text color="neutral.500" fontSize="sm" textAlign="center">
          {t("components.lists.searchableMultiSelect.noListsCreatedYet")}
        </Text>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" overflowY="hidden" spacing={3}>
      {/*
        size="sm" lives on the InputGroup so both InputLeftElement and
        Input size to the same height — without it, the InputLeftElement
        defaults to md and the icon sits above the placeholder's
        baseline (vertical-center math uses the default height).
      */}
      <InputGroup size="sm">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="dune.100" w={4} h={4} />
        </InputLeftElement>
        <Input
          placeholder={t(
            "components.lists.searchableMultiSelect.searchPlaceholder",
          )}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          bg="transparent.white.10"
          border="1px solid"
          borderColor="transparent.white.20"
          color="neutral.200"
          _placeholder={{ color: "neutral.500" }}
          _focus={{
            borderColor: "blaze.300",
            bg: "transparent.white.20",
            outline: "none",
            boxShadow: "none",
          }}
        />
      </InputGroup>

      <Stack
        spacing={0}
        flex="1"
        minHeight="0"
        // Bumped from 200px → 340px so users with ~6 lists don't have to
        // scroll inside the popover. Bounded so a user with hundreds of
        // lists still gets a contained surface rather than an unbounded
        // scrollport pushing the footer off-screen.
        maxHeight="340px"
        overflowY="auto"
      >
        {filteredLists.length === 0 ? (
          <Text color="neutral.500" fontSize="sm" textAlign="center" py={4}>
            {t("components.lists.searchableMultiSelect.noResults")}
          </Text>
        ) : (
          filteredLists.map((list, i) => {
            const inList = isEntityInList(list);
            const isChecked = selectedSet.has(list.id);
            const coverUrls = pickListCovers(list, listType);
            const itemCount = list.total_items ?? list.list_items?.length ?? 0;
            // Stat line — kept terse since the row is narrow and the
            // cover stack carries most of the visual identity. We don't
            // surface views here (the popover is a curate action, not a
            // browse surface); item count + "In list" is the right
            // signal density.
            const statLabel = t(
              listType === "releases"
                ? itemCount === 1
                  ? "components.lists.searchableMultiSelect.statReleases_one"
                  : "components.lists.searchableMultiSelect.statReleases_other"
                : itemCount === 1
                  ? "components.lists.searchableMultiSelect.statComics_one"
                  : "components.lists.searchableMultiSelect.statComics_other",
              { count: itemCount },
            );
            return (
              <Box key={list.id}>
                <ListCheckbox
                  {...getCheckboxProps({ value: list.id.toString() })}
                >
                  {/*
                    Mini cover stack — visual anchor on the left. Same
                    65:100 cover aspect the rest of the lists surface
                    uses, scaled tiny (52px tall). Up to 3 covers
                    staggered with a shallow X+Y offset (4px / 3px) so
                    the row stays compact while still reading as a
                    stacked collection rather than a single thumbnail.
                  */}
                  <MiniCoverStack covers={coverUrls} />
                  <Flex
                    flex="1"
                    minW={0}
                    direction="column"
                    justify="center"
                    gap="2px"
                    py="2px"
                  >
                    <Flex align="center" gap="6px" minW={0}>
                      <Text
                        fontFamily="Bricolage Grotesque"
                        fontSize="14px"
                        fontWeight={500}
                        lineHeight="1.25"
                        color={isChecked ? "dune.dune" : "dune.100"}
                        _groupHover={{ color: "dune.dune" }}
                        sx={{
                          fontVariationSettings: '"opsz" 14, "wdth" 100',
                        }}
                        noOfLines={1}
                        wordBreak="break-word"
                      >
                        {list.display_name}
                      </Text>
                      {!list.is_public && (
                        <Tooltip
                          label={t(
                            "components.lists.searchableMultiSelect.privateAria",
                          )}
                          openDelay={300}
                        >
                          <Box
                            as="span"
                            aria-label={t(
                              "components.lists.searchableMultiSelect.privateAria",
                            )}
                            color="neutral.400"
                            display="inline-flex"
                            flexShrink={0}
                          >
                            <LockIcon w="12px" h="12px" />
                          </Box>
                        </Tooltip>
                      )}
                    </Flex>
                    {/*
                      Meta line — item count and (if member) "In list"
                      sit on the same row separated by a hairline middle
                      dot, matching the shelf rail's StatsLine treatment.
                      "In list" gets the blaze accent so an at-a-glance
                      scan of the popover surfaces "what's this thing
                      already in".
                    */}
                    <Flex
                      align="center"
                      fontFamily="Roboto"
                      fontSize="11px"
                      lineHeight="1.3"
                      letterSpacing="0.15px"
                      color="neutral.400"
                      gap="6px"
                    >
                      <Text as="span">{statLabel}</Text>
                      {inList && (
                        <>
                          <Text
                            as="span"
                            aria-hidden="true"
                            color="neutral.500"
                          >
                            ·
                          </Text>
                          <Text
                            as="span"
                            color="blaze.300"
                            fontWeight={600}
                          >
                            {t(
                              "components.lists.searchableMultiSelect.inListBadge",
                            )}
                          </Text>
                        </>
                      )}
                    </Flex>
                  </Flex>
                </ListCheckbox>
                {i < filteredLists.length - 1 && (
                  <Box
                    w="full"
                    h="1px"
                    bg="transparent.white.05"
                    ml="60px"
                  />
                )}
              </Box>
            );
          })
        )}
      </Stack>
    </VStack>
  );
}

interface ListCheckboxProps extends UseCheckboxProps {
  children?: React.ReactNode;
}

function ListCheckbox(props: ListCheckboxProps) {
  const { state, getInputProps } = useCheckbox(props);
  const input = getInputProps();

  return (
    <Box
      layerStyle="filterList"
      as="label"
      cursor="pointer"
      role="group"
      px={2}
      py={2}
      display="flex"
      alignItems="center"
      gap={3}
      transition="background 120ms ease-out"
      _hover={{ bg: "transparent.white.03" }}
    >
      <input {...input} hidden />
      {props.children}
      {state.isChecked ? (
        <CheckboxCheckedIcon
          color="blaze.300"
          w={5}
          h={5}
          flexShrink={0}
          ml="auto"
        />
      ) : (
        <CheckboxUncheckedIcon
          _groupHover={{ color: "transparent.white.80" }}
          color="transparent.white.60"
          w={5}
          h={5}
          flexShrink={0}
          ml="auto"
        />
      )}
    </Box>
  );
}

// ---------- Cover-stack helpers ----------

// Pull up to 3 cover thumbnail URLs from a list, preferring the flat
// `covers[]` shipped by listing endpoints over the per-item walk of
// `list_items` (which is what /v1/lists/comics + /releases still hand us
// since we deliberately didn't truncate those endpoints). Resolves
// release items via their `thumbnail_url` field (same field the browse
// surfaces render) and comic items via the standard image_*_url chain.
function pickListCovers(
  list: List,
  listType: "comics" | "releases",
): string[] {
  if (list.covers && list.covers.length > 0) {
    return list.covers.slice(0, 3).filter((u): u is string => !!u);
  }
  const items = list.list_items ?? [];
  return items
    .slice(0, 3)
    .map((item) => coverUrlForItem(item, listType))
    .filter((u): u is string => u != null);
}

function coverUrlForItem(
  item: ListItem,
  listType: "comics" | "releases",
): string | undefined {
  if (listType === "releases") {
    return (
      item.release?.thumbnail_url ??
      item.release?.image_small_url ??
      item.release?.image_tiny_url ??
      item.release?.image_url
    );
  }
  return (
    item.comic?.image_small_url ??
    item.comic?.image_tiny_url ??
    item.comic?.image_url
  );
}

const MINI_COVER_W = 34; // 65:100 of MINI_COVER_H ≈ 22 — bumped to 34 for legibility
const MINI_COVER_H = 52;
const MINI_STAGGER_X = 4;
const MINI_STAGGER_Y = 3;

interface MiniCoverStackProps {
  covers: string[];
}

// Compact 3-cover stack rendered inline in each picker row. Distinct
// component from ReadingListDetailHeroCoverStack — that one is built
// around large staggered offsets (22×34 natural) and a MIN_SCALE of
// 0.5; clamping it down to row-thumbnail size would either bottom-out
// at the clamp or distort the stagger. Cheaper to ship a purpose-built
// mini variant that uses a flatter 4×3 stagger and a fixed 65:100
// aspect — visually consistent with the detail surface, sized right
// for a 52px row.
function MiniCoverStack({ covers }: MiniCoverStackProps) {
  const visible = covers.slice(0, 3);
  const stackW =
    MINI_COVER_W + MINI_STAGGER_X * Math.max(0, visible.length - 1);
  const stackH =
    MINI_COVER_H + MINI_STAGGER_Y * Math.max(0, visible.length - 1);

  return (
    <Box
      position="relative"
      width={`${stackW}px`}
      height={`${stackH}px`}
      flexShrink={0}
    >
      {visible.length === 0 ? (
        // Placeholder rectangle so the row footprint matches when a list
        // has no items yet. Same 65:100 envelope so an empty list still
        // reads as "a slot for covers" rather than missing chrome.
        <Box
          position="absolute"
          inset={0}
          width={`${MINI_COVER_W}px`}
          height={`${MINI_COVER_H}px`}
          bg="transparent.white.05"
          border="1px solid"
          borderColor="transparent.white.10"
          borderRadius="3px"
        />
      ) : (
        visible.map((url, i) => (
          <Box
            key={`${i}-${url}`}
            position="absolute"
            left={`${i * MINI_STAGGER_X}px`}
            top={`${i * MINI_STAGGER_Y}px`}
            width={`${MINI_COVER_W}px`}
            height={`${MINI_COVER_H}px`}
            borderRadius="3px"
            overflow="hidden"
            border="1px solid"
            borderColor="transparent.white.20"
            boxShadow={
              i === visible.length - 1
                ? "2px 2px 6px rgba(0,0,0,0.45)"
                : "1px 1px 3px rgba(0,0,0,0.35)"
            }
            zIndex={i + 1}
          >
            <ProtectedImage
              src={url}
              alt=""
              position="absolute"
              inset={0}
              w="100%"
              h="100%"
              objectFit="cover"
            />
          </Box>
        ))
      )}
    </Box>
  );
}
